import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, MapPin, Trash2, Pencil, Share2, FileText, Copy } from "lucide-react";
import { Navbar } from "../components/Navbar";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Itineraries() {
  const [rows, setRows] = useState(null);
  const [del, setDel] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/itineraries");
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/itineraries/${del.id}`);
      toast.success("Itinerary deleted.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDel(null); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/itineraries/${id}/duplicate`);
      toast.success("Itinerary duplicated. Opening the copy…");
      navigate(`/itineraries/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Itineraries</h1>
            <p className="text-slate-500 mt-1">Saved, branded itineraries linked to your account.</p>
          </div>
          <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-itinerary-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Create Itinerary
          </Button>
        </div>

        {rows === null ? (
          <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div data-testid="itin-empty" className="mt-8 rounded-lg border border-dashed border-slate-200 bg-white py-16 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <div className="font-heading font-semibold text-slate-700">No itineraries yet</div>
            <p className="text-sm text-slate-500 mt-1">Convert a PNR and click “Save as itinerary” to get started.</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((it) => (
              <div key={it.id} data-testid={`itin-${it.id}`} className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="font-heading font-bold text-slate-900">{it.title}</div>
                  {it.share_enabled && <Badge variant="outline" className="rounded-full border-emerald-200 bg-emerald-50 text-emerald-700 text-xs"><Share2 className="h-3 w-3 mr-1" />Shared</Badge>}
                </div>
                <div className="text-sm text-slate-500 mt-1 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {it.destination_city || "—"}</div>
                <div className="text-xs text-slate-400 mt-1">{it.gds || ""} · {new Date(it.updated_at).toLocaleDateString()}</div>
                <div className="flex-1" />
                <div className="flex items-center gap-2 mt-4">
                  <Button size="sm" onClick={() => navigate(`/itineraries/${it.id}`)} className="bg-slate-900 hover:bg-slate-800 text-white flex-1" data-testid={`open-${it.id}`}>
                    <Pencil className="h-4 w-4 mr-1.5" /> Open
                  </Button>
                  <Button size="icon" variant="ghost" className="text-slate-500 hover:text-blue-600" onClick={() => duplicate(it.id)} data-testid={`duplicate-${it.id}`} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setDel(it)} data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete itinerary?</AlertDialogTitle>
            <AlertDialogDescription>“{del?.title}” will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-itin" onClick={remove} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
